import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { FieldLabel, ToggleChip } from "@/components/admin/shared/UIPrimitives";

export default function RegEditDialog({ reg, onClose, onSaved }) {
  const [form, setForm] = useState({
    student_name: reg.student_name || "",
    parent_name: reg.parent_name || "",
    parent_email: reg.parent_email || "",
    parent_phone: reg.parent_phone || "",
    esa_contract_number: reg.esa_contract_number || "",
    admin_notes: reg.admin_notes || "",
    allergies_notes: reg.allergies_notes || "",
    add_on_delivered: !!reg.add_on_delivered,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/registrations/${reg.id}`, form);
      toast.success("Registration updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  const del = async () => {
    if (!confirm(`Permanently DELETE registration for ${reg.student_name}? This releases the spot. Use Archive instead if you want to keep the record.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/registrations/${reg.id}`);
      toast.success("Registration deleted, spot released");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-gg-surface max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Edit registration · #{reg.invoice_number}</DialogTitle>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <FieldLabel label="Student name">
            <Input value={form.student_name} onChange={set("student_name")} data-testid="edit-reg-student-name" />
          </FieldLabel>
          <FieldLabel label="ESA contract #">
            <Input value={form.esa_contract_number} onChange={set("esa_contract_number")} data-testid="edit-reg-esa" />
          </FieldLabel>
          <FieldLabel label="Parent name">
            <Input value={form.parent_name} onChange={set("parent_name")} />
          </FieldLabel>
          <FieldLabel label="Parent email">
            <Input type="email" value={form.parent_email} onChange={set("parent_email")} />
          </FieldLabel>
          <FieldLabel label="Parent phone" className="sm:col-span-2">
            <Input value={form.parent_phone} onChange={set("parent_phone")} />
          </FieldLabel>
          <FieldLabel label="⚠ Allergies / dietary / accommodations (from parent)" className="sm:col-span-2">
            <Textarea
              value={form.allergies_notes}
              onChange={set("allergies_notes")}
              rows={2}
              placeholder="Submitted by parent. You can edit."
              data-testid="edit-reg-allergies"
            />
          </FieldLabel>
          <FieldLabel label="📝 Private admin notes" className="sm:col-span-2">
            <Textarea
              value={form.admin_notes}
              onChange={set("admin_notes")}
              rows={3}
              placeholder="Allergies, scheduling quirks, referral source, follow-up needed…"
              data-testid="edit-reg-notes"
            />
          </FieldLabel>
          <div className="sm:col-span-2 flex flex-wrap gap-2 pt-2">
            {reg.parent_attending && (
              <ToggleChip
                checked={form.add_on_delivered}
                onClick={() => set("add_on_delivered")(!form.add_on_delivered)}
                label={form.add_on_delivered ? "✓ Parent add-on delivered" : "Parent add-on pending"}
              />
            )}
          </div>
        </div>
        <DialogFooter className="mt-4 flex justify-between gap-2 flex-wrap">
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={async () => {
                if (!confirm(`Resend invoice email to ${reg.parent_email}?`)) return;
                try {
                  await api.post(`/admin/registrations/${reg.id}/resend-invoice`, {});
                  toast.success(`Invoice email sent to ${reg.parent_email}`);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Could not send");
                }
              }}
              disabled={busy}
              className="btn-pill btn-outline text-gg-green border-gg-green/40 hover:bg-gg-green/10"
              data-testid="edit-reg-resend-invoice"
            >
              Resend invoice email
            </button>
            <button type="button" onClick={del} disabled={busy} className="btn-pill btn-outline text-[#B24C4C] border-[#B24C4C]/40 hover:bg-[#B24C4C]/10" data-testid="edit-reg-delete">
              Delete &amp; release spot
            </button>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-pill btn-outline">Cancel</button>
            <button type="button" onClick={save} disabled={busy} className="btn-pill btn-primary disabled:opacity-60" data-testid="edit-reg-save">
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
